import React, { useEffect, useState } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { adminApi } from '../../lib/api';
import { formatDateTime, ROLE_LABELS } from '../../lib/utils';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Edit, ShieldCheck, UserX } from 'lucide-react';
import { LoadingBlock, EmptyState } from '../../components/common/States';
import { useAuth } from '../../App';

const ROLE_COLORS = {
  super_admin: 'text-[#D4A843] bg-[#D4A843]/10 border-[#D4A843]/30',
  manager: 'text-[#4AA3DF] bg-[#4AA3DF]/10 border-[#4AA3DF]/30',
  reception: 'text-[#2FBF71] bg-[#2FBF71]/10 border-[#2FBF71]/30',
  finance_viewer: 'text-[#E6B450] bg-[#E6B450]/10 border-[#E6B450]/30',
  marketing: 'text-[#B084EB] bg-[#B084EB]/10 border-[#B084EB]/30',
  gate_scan: 'text-[#7AD3F2] bg-[#7AD3F2]/10 border-[#7AD3F2]/30',
  special_ops: 'text-[#F1B84A] bg-[#F1B84A]/10 border-[#F1B84A]/30',
};

const DEFAULT_FORM = { name: '', email: '', password: '', role: 'reception' };

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getUsers();
      setUsers(res.data.users);
    } catch { toast.error('Access denied or failed to load users'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const openCreate = () => {
    setEditingUser(null);
    setForm(DEFAULT_FORM);
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setForm({ name: u.name, email: u.email, password: '', role: u.role });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingUser && (!form.name || !form.email || !form.password)) {
      toast.error('Name, email and password are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editingUser && !payload.password) delete payload.password;
      if (editingUser) {
        await adminApi.updateUser(editingUser._id, payload);
        toast.success('User updated');
      } else {
        await adminApi.createUser(payload);
        toast.success('User created');
      }
      setDialogOpen(false);
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await adminApi.updateUser(u._id, { is_active: !u.is_active });
      setUsers(us => us.map(user => user._id === u._id ? { ...user, is_active: !u.is_active } : user));
      toast.success(u.is_active ? 'User deactivated' : 'User activated');
    } catch { toast.error('Failed to update user'); }
  };

  return (
    <AdminShell
      title="User Management"
      actions={
        <Button size="sm" className="btn-gold gap-1.5 text-xs h-8" onClick={openCreate} data-testid="create-user-button">
          <Plus size={13} /> New User
        </Button>
      }
    >
      <div className="space-y-4">
        {loading ? (
          <LoadingBlock rows={4} />
        ) : users.length === 0 ? (
          <EmptyState title="No users found" action={openCreate} actionLabel="Create User" />
        ) : (
          <div className="surface-card card-highlight rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#2A2A2A]">
                  {['Name', 'Email', 'Role', 'Status', 'Created', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs text-[#6F6F6F] uppercase tracking-wider font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E1E1E]">
                {users.map(u => (
                  <tr key={u._id} className="table-row-hover">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-[#D4A843]/15 border border-[#D4A843]/30 flex items-center justify-center">
                          <span className="text-[#D4A843] text-xs font-semibold">{u.name?.charAt(0).toUpperCase()}</span>
                        </div>
                        <span className="text-[#F2F2F2] font-medium">{u.name}</span>
                        {u._id === currentUser?.id && <span className="text-[8px] text-[#D4A843] bg-[#D4A843]/10 px-1.5 py-0.5 rounded-full uppercase">You</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#B9B9B9]">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${ROLE_COLORS[u.role] || ''}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Switch
                        checked={u.is_active}
                        onCheckedChange={() => handleToggleActive(u)}
                        disabled={u._id === currentUser?.id}
                      />
                    </td>
                    <td className="px-4 py-3 text-[#6F6F6F] text-xs">{formatDateTime(u.created_at)}</td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant="ghost" className="text-xs text-[#B9B9B9] h-7 px-2" onClick={() => openEdit(u)}>
                        <Edit size={12} className="mr-1" /> Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-['EB_Garamond'] text-xl text-[#F2F2F2]">
              {editingUser ? 'Edit User' : 'Create User'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label className="text-[#B9B9B9] text-xs">Full Name</Label>
              <Input value={form.name} onChange={e => setForm(f=>({...f, name:e.target.value}))} className="bg-[#111111] border-[#2A2A2A]" placeholder="Jane Smith" />
            </div>
            <div className="space-y-1">
              <Label className="text-[#B9B9B9] text-xs">Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm(f=>({...f, email:e.target.value}))} className="bg-[#111111] border-[#2A2A2A]" placeholder="jane@example.com" />
            </div>
            <div className="space-y-1">
              <Label className="text-[#B9B9B9] text-xs">{editingUser ? 'New Password (leave blank to keep)' : 'Password'}</Label>
              <Input type="password" value={form.password} onChange={e => setForm(f=>({...f, password:e.target.value}))} className="bg-[#111111] border-[#2A2A2A]" placeholder="••••••••" />
            </div>
            <div className="space-y-1">
              <Label className="text-[#B9B9B9] text-xs">Role</Label>
              <Select value={form.role} onValueChange={v => setForm(f=>({...f, role:v}))}>
                <SelectTrigger className="bg-[#111111] border-[#2A2A2A]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                  {Object.entries(ROLE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-[#B9B9B9]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-gold">
              {saving ? 'Saving...' : editingUser ? 'Update' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
